import { useCallback, useState } from 'react';

import { CreateTaskGroupInput, TaskGroup } from '@this+that/models';
import log from 'loglevel';

import { useCreateTaskGroup as useCreateTaskGroupService } from '../../services/taskGroups.service';
import { useInboxEntriesStore } from '../../store';

interface UseCreateTaskGroupReturn {
  handleCreateTaskGroup: (
    taskGroup: CreateTaskGroupInput,
  ) => Promise<TaskGroup | undefined>;
  isCreating: boolean;
  createError: Error | null;
}

export function useCreateTaskGroup(): UseCreateTaskGroupReturn {
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<Error | null>(null);

  const { createTaskGroup: createTaskGroupService } =
    useCreateTaskGroupService();
  const { createTaskGroup } = useInboxEntriesStore(
    (state) => state.taskManager.taskGroups,
  );

  const handleCreateTaskGroup = useCallback(
    async (taskGroup: CreateTaskGroupInput): Promise<TaskGroup | undefined> => {
      setIsCreating(true);
      setCreateError(null);
      try {
        const createdTaskGroup = await createTaskGroupService(taskGroup);
        if (createdTaskGroup) {
          createTaskGroup(createdTaskGroup);
        }
        return createdTaskGroup;
      } catch (error) {
        const errorInstance =
          error instanceof Error
            ? error
            : new Error('Failed to create task group');
        setCreateError(errorInstance);
        log.error('Error creating task group:', error);
        return undefined;
      } finally {
        setIsCreating(false);
      }
    },
    [createTaskGroupService, createTaskGroup],
  );

  return {
    handleCreateTaskGroup,
    isCreating,
    createError,
  };
}
